import { useEffect } from 'react';

// Tells the native app when a sheet or dialog is open, and closes the top one when it asks.
//
// The app's back (Android's hardware button, and the chevron in the native top bar on both
// platforms) steps back through the shell's own page history. It had no idea a dialog was open,
// so on the emulator, back with the «Nueva solicitud» form half filled went to the previous page
// and the form was gone. On Android, back is expected to close the sheet first.
//
// So: whenever the answer to "is a dialog open?" changes, post MODAL_STATE. The shell
// (App.js goBack) closes the dialog instead of navigating while one is open, by dispatching
// somostorre:close-modal, which becomes the Escape key every Radix dialog, sheet and select
// already listens for, and which closes only the topmost layer. Older shells ignore the message
// and behave exactly as before.
const OPEN = '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]';

export default function useNativeModalBridge() {
  useEffect(() => {
    const bridge = window.ReactNativeWebView;
    if (!bridge) return undefined;
    let last = null;
    let queued = false;
    const report = () => {
      queued = false;
      const open = !!document.querySelector(OPEN);
      if (open === last) return;
      last = open;
      try { bridge.postMessage(JSON.stringify({ type: 'MODAL_STATE', open })); } catch { /* bridge gone */ }
    };
    // One check per frame at most: list pages re-render often, and each batch of mutations would
    // otherwise run the query again.
    const schedule = () => { if (!queued) { queued = true; requestAnimationFrame(report); } };
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-state'] });
    report();
    const close = () => {
      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true, cancelable: true }));
    };
    window.addEventListener('somostorre:close-modal', close);
    return () => {
      observer.disconnect();
      window.removeEventListener('somostorre:close-modal', close);
    };
  }, []);
}
